import { useState, useEffect } from 'react';
import { Upload, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { adminAPI } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CMSManager() {
  const [settings, setSettings] = useState({
    logo_url: null,
    hero_title: '',
    hero_subtitle: '',
    about_text: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminAPI.getCMSSettings();
      setSettings(response.data);
      if (response.data.logo_url) {
        setLogoPreview(`${BACKEND_URL}${response.data.logo_url}`);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      const response = await adminAPI.uploadLogo(formData);
      
      // Update settings with new logo URL
      const newSettings = { ...settings, logo_url: response.data.logo_url };
      setSettings(newSettings);
      setLogoFile(null);
      setLogoPreview(`${BACKEND_URL}${response.data.logo_url}`);
      
      toast.success('Logo uploaded successfully! Refresh homepage to see changes.');
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await adminAPI.updateCMSSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-600">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-slate-900">Website Settings</h3>

      {/* Logo Upload */}
      <Card className="p-6 bg-white rounded-xl border border-slate-100 shadow-sm">
        <h4 className="text-lg font-semibold text-slate-900 mb-4">Website Logo</h4>
        <div className="flex items-center gap-6">
          <div className="w-48 h-20 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-slate-200">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-slate-400 text-sm">No logo</span>
            )}
          </div>
          <div className="flex-1">
            <Input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="mb-2"
            />
            <p className="text-xs text-slate-500 mb-3">Recommended: PNG or SVG, max 200x80px</p>
            <Button
              onClick={handleLogoUpload}
              disabled={!logoFile}
              className="bg-teal-600 text-white hover:bg-teal-700 rounded-lg"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Logo
            </Button>
          </div>
        </div>
      </Card>

      {/* Hero Section */}
      <Card className="p-6 bg-white rounded-xl border border-slate-100 shadow-sm">
        <h4 className="text-lg font-semibold text-slate-900 mb-4">Hero Section</h4>
        <div className="space-y-4">
          <div>
            <Label htmlFor="hero_title">Hero Title</Label>
            <Input
              id="hero_title"
              value={settings.hero_title}
              onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
              className="mt-1"
              placeholder="Экспертное второе мнение по радиологии"
            />
          </div>
          <div>
            <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
            <Input
              id="hero_subtitle"
              value={settings.hero_subtitle}
              onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
              className="mt-1"
              placeholder="Свяжитесь с лицензированными специалистами-радиологами"
            />
          </div>
        </div>
      </Card>

      {/* About Section */}
      <Card className="p-6 bg-white rounded-xl border border-slate-100 shadow-sm">
        <h4 className="text-lg font-semibold text-slate-900 mb-4">About Section</h4>
        <div>
          <Label htmlFor="about_text">About Text</Label>
          <textarea
            id="about_text"
            value={settings.about_text}
            onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary mt-1"
            placeholder="Information about your platform..."
          />
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-8"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      {/* Preview Note */}
      <Card className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Changes to hero title and subtitle will be visible on the homepage after saving. 
          The logo will update immediately after upload. Refresh the homepage to see changes.
        </p>
      </Card>
    </div>
  );
}
